import React, { memo, useMemo, useState } from 'react';
import { Tooltip, Fade } from '@mui/material';
import PropTypes from 'prop-types';
import { get, size } from 'lodash';
import { getHintMarkerBaloonPos } from './baloonDimensionService';
import flashIcon from '../../../../images/markers/flash.svg';
import uiLabelsPaths from '../../../../common/services/uiFieldLabelsDictionary';
import { statusLabels } from '../../../../common/constants/statuses';
import { getShowTechnicalInfoFieldsFlag } from '../../../../common/services/AuthenticationRules';

const ClusterDetailsPopup = memo(
  ({
    data,
    openChangerModal,
    isMobile,
    mobileHover,
    cascadingStyle,
    config,
    toggleZoom,
    id,
    onTouchStart,
    children,
    $getDimensions,
    $dimensionKey,
    $geoService,
  }) => {
    const [isPopoverOpen, setPopoverOpen] = useState(false);

    const { ui_labels: uiLabels } = config;
    const isTechnicalInfoFieldsFlagAvailable = getShowTechnicalInfoFieldsFlag(config);

    const hintBaloonPos = useMemo(() => {
      const markerDim = $getDimensions($dimensionKey);
      const mapWidth = $geoService.getWidth();

      return getHintMarkerBaloonPos(345, 110, markerDim.x, markerDim.y, mapWidth);
    }, [$getDimensions, $dimensionKey, $geoService]);

    const handleTouchStart = isMobile
      ? () => {
          setPopoverOpen(true);
          onTouchStart(id);
        }
      : null;
    const handleMouseEnter = isMobile
      ? null
      : (event) => {
          toggleZoom(false);
          setPopoverOpen(true);
        };

    const handleRequestClose = (forceClose = false) => {
      if (!isMobile || !mobileHover || forceClose) {
        toggleZoom(true);
        setPopoverOpen(false);
      }
    };

    return (
      <Tooltip
        open={isPopoverOpen && (!isMobile || mobileHover)}
        onClose={() => handleRequestClose()}
        disableHoverListener={isMobile}
        TransitionComponent={Fade}
        placement={hintBaloonPos}
        slotProps={{
          tooltip: {
            style: {
              padding: '8px 10px',
            },
          },
          popper: {
            style: {
              boxShadow: 'none',
            },
          },
        }}
        arrow
        title={
          size(data) ? (
            <div className="i2p2-marker-content cluster-details">
              {data.map((marker) => (
                <div className="repeated-info" key={marker.id}>
                  <div className="container">
                    <img className="flash-icon" src={flashIcon} alt="" />
                    <strong className="name-caption">
                      Name:
                      {marker.data.name}
                    </strong>
                  </div>
                  <div className="info-container">
                    <p>
                      {`${get(uiLabels, uiLabelsPaths.c_uuid, 'Charger ID')}: `}
                      <span>{marker.id}</span>
                    </p>
                    <p>
                      {`${get(uiLabels, uiLabelsPaths.c_status, 'Status')}: `}
                      <span className="capitalized">{statusLabels[marker.data.state]}</span>
                    </p>
                    <p>
                      {`${get(uiLabels, uiLabelsPaths.c_online, 'Online')}: `}
                      <span className="capitalized">{marker.data.online}</span>
                    </p>
                    <p>
                      {`${get(uiLabels, uiLabelsPaths.connector_state, 'State')}: `}
                      <span className="capitalized">{marker.data.chargerState}</span>
                    </p>
                    <p>
                      {`${get(uiLabels, uiLabelsPaths.p_type, 'Product Type')}: `}
                      <span>{marker.data.productType}</span>
                    </p>

                    {isTechnicalInfoFieldsFlagAvailable && (
                      <p>
                        {`${get(uiLabels, uiLabelsPaths.c_product_version, 'Product Version')}: `}
                        <span className="capitalized">{marker.data.productVersion}</span>
                      </p>
                    )}

                    <p>
                      {`${get(uiLabels, uiLabelsPaths.product_serial, `${marker.data.productName} Serial Number`)}: `}
                      <span className="capitalized">{marker.data.productSerialNumber}</span>
                    </p>
                  </div>
                  <div>
                    <p
                      className="text-center link"
                      onClick={() => {
                        openChangerModal(marker);
                        handleRequestClose(true);
                      }}
                    >
                      Click to view more info
                    </p>
                    <div className="full-width-border" />
                  </div>
                </div>
              ))}
            </div>
          ) : null
        }
      >
        <div
          style={cascadingStyle}
          onTouchStart={handleTouchStart}
          onMouseEnter={handleMouseEnter}
          className={`i2p2-marker ${isMobile ? 'details-mobile' : ''}`}
        >
          {children}
        </div>
      </Tooltip>
    );
  },
);

ClusterDetailsPopup.propTypes = {
  onTouchStart: PropTypes.func,
  toggleZoom: PropTypes.func,
  openChangerModal: PropTypes.func,
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  id: PropTypes.string,
};

export default ClusterDetailsPopup;
